<template>
  <v-container fluid>
    <v-row no-gutters>
      <v-col cols="12">
        <v-card :loading="loading" class="bg-grey-lighten-4 elevation-0">
          <v-toolbar color="primary" density="comfortable">
            <v-toolbar-title class="text-white">
              {{ title }}
            </v-toolbar-title>
            <v-spacer />
            <div class="d-flex ga-2">
              <v-tooltip v-if="!embedded" location="left">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" @click="cancel">
                    <v-icon>mdi-close-circle-outline</v-icon>
                  </v-btn>
                </template>
                Fermer
              </v-tooltip>

              <v-tooltip location="left">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" @click="save">
                    <v-icon>mdi-content-save-all</v-icon>
                  </v-btn>
                </template>
                Sauvegarder
              </v-tooltip>
              <v-tooltip location="left">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" @click="createNewPanne">
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </template>
                Créer une panne
              </v-tooltip>
              <v-tooltip v-if="canBeReplaced" location="left">
                <template #activator="{ props }">
                  <v-btn v-bind="props" icon variant="text" @click="openReplacementDialog">
                    <v-icon>mdi-swap-horizontal</v-icon>
                  </v-btn>
                </template>
                Remplacer
              </v-tooltip>
            </div>
          </v-toolbar>
        </v-card>
      </v-col>
    </v-row>

    <!-- PANELS -->
    <v-expansion-panels>
      <!-- INFORMATIONS -->
      <v-expansion-panel>
        <v-expansion-panel-title class="text-primary"> Informations </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-row align="stretch">
            <!-- Left section -->
            <v-col cols="8">
              <v-row density="compact">
                <v-col cols="6">
                  <v-autocomplete v-model="product.productMake" :items="productMakes" label="Fabricant *" />
                </v-col>
                <v-col cols="6">
                  <v-autocomplete v-model="product.productType" :items="productTypes" label="Type *" />
                </v-col>
              </v-row>

              <v-row density="compact">
                <v-col cols="6">
                  <v-text-field v-model="product.aquaNb" type="number" label="N° aqua *" />
                </v-col>
                <v-col cols="6">
                  <v-autocomplete v-model="product.productModelId" :items="productModels" item-title="model"
                    item-value="id" label="Modèle *" />
                </v-col>
              </v-row>

              <v-row density="compact">
                <v-col cols="6">
                  <v-text-field v-model="product.price" type="number" label="Prix d'achat" />
                </v-col>
                <v-col cols="6">
                  <v-text-field v-model="product.serialNb" label="N° Série *" />
                </v-col>
              </v-row>

              <v-row density="compact">
                <v-col cols="6">
                  <v-text-field v-model="product.bonEntreeDate" type="date" label="Date Bon d'entrée" />
                </v-col>
                <v-col cols="6">
                  <v-text-field v-model="product.bonEntreeNb" label="N° Bon d'entrée" />
                </v-col>
              </v-row>

              <v-row density="compact">
                <v-col cols="6">
                  <v-text-field v-model="product.compteur" type="number" label="Compteur" />
                </v-col>
                <v-col cols="6">
                  <v-autocomplete v-model="product.productStatus" :items="productStatus" label="Status *" />
                </v-col>
              </v-row>

              <v-row density="compact">
                <v-col cols="6">
                  <v-autocomplete v-model="product.reservationStatus" :items="reservationStatus"
                    label="Disponibilité *" />
                </v-col>
                <v-col cols="6" v-if="isDestructedReservationStatus">
                  <v-text-field v-model="product.destructionDate" type="date" label="Date de destruction *" />
                </v-col>
              </v-row>

              <v-row density="compact">
                <!-- A new product enters the stock of a depot, it reaches a laverie through a preparation task -->
                <v-col v-if="isLocationReadOnly" cols="6">
                  <v-autocomplete v-model="product.laverieId" :items="laveries" item-title="numeroDeContrat"
                    item-value="id" label="Laverie" readonly :hint="locationHint" persistent-hint />
                </v-col>
                <v-col :cols="isLocationReadOnly ? 6 : 12">
                  <v-autocomplete v-model="product.storageDepotId" :items="storageDepots" item-title="name"
                    item-value="id" :readonly="isLocationReadOnly" :hint="locationHint" persistent-hint>
                    <template #label>
                      Dépôt<span v-if="!isLocationReadOnly" class="text-error"><strong> *</strong></span>
                    </template>
                  </v-autocomplete>
                </v-col>
              </v-row>

              <v-row v-if="product.lifecycleStageLabel" density="compact">
                <v-col cols="6">
                  <v-text-field :model-value="product.lifecycleStageLabel" label="Cycle de vie" readonly />
                </v-col>
              </v-row>
            </v-col>

            <!-- Right section -->
            <v-col cols="4" class="right-section d-flex flex-column">
              <!-- Similar available products -->
              <div class="text-subtitle-2 font-weight-medium mb-1 text-primary">Produits disponibles similaires ({{
                similarProducts.length }})</div>
              <div class="right-section-scroll">
                <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-2" />
                <div v-else-if="similarProducts.length === 0" class="text-body-2 text-medium-emphasis">
                  Aucun produit disponible
                </div>
                <v-list v-else density="compact" class="pa-0">
                  <v-list-item v-for="p in similarProducts" :key="p.id" class="bg-grey-lighten-3 mb-1 rounded"
                    style="cursor: pointer" @click="router.push({ name: 'product', params: { id: p.id } })">
                    <v-list-item-title class="text-body-2 font-weight-medium">N° {{ p.aquaNb }}</v-list-item-title>
                    <v-list-item-subtitle class="text-caption">{{ p.serialNb }}</v-list-item-subtitle>
                  </v-list-item>
                </v-list>
              </div>
            </v-col>
          </v-row>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title class="text-primary"> Notifications </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-switch v-model="product.notificationEnabled" color="primary" label="Activer les notifications" />
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- PANNES -->
      <v-expansion-panel>
        <v-expansion-panel-title class="text-primary"> Pannes </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-list>
            <v-list-item v-for="p in pannes" :key="p.id" class="bg-grey-lighten-2 my-2">
              <v-list-item-title class="text-blue">
                {{ p.name }}
              </v-list-item-title>
              <v-list-item-subtitle>
                <div class="d-flex flex-column ga-4">
                  <div>
                    <div v-if="p.desc" class="text-body-2 text-medium-emphasis">
                      {{ p.desc }}
                    </div>
                    <div class="mt-2 text-body-2">
                      <div><strong>Date de création:</strong> {{ formatDate(p.creationDate) }}</div>
                      <div><strong>Réparée:</strong> {{ p.repaired ? "Oui" : "Non" }}</div>
                      <div v-if="p.repaired"><strong>Date de réparation:</strong> {{ formatDate(p.reparationDate) }}
                      </div>
                    </div>
                  </div>
                  <div>
                    <div class="text-subtitle-2 font-weight-medium mb-1">Pièces installées</div>
                    <div v-if="!p.installedSpareParts || p.installedSpareParts.length === 0"
                      class="text-body-2 text-medium-emphasis">
                      Aucune pièce installée
                    </div>
                    <v-list v-else density="compact" class="bg-transparent pa-0">
                      <v-list-item v-for="sparePart in p.installedSpareParts" :key="sparePart.id" class="px-0">
                        <v-list-item-title>
                          {{ sparePart.displayName || sparePart.sparepartId }}
                        </v-list-item-title>
                        <v-list-item-subtitle>
                          Quantite: {{ sparePart.quantity }}
                        </v-list-item-subtitle>
                      </v-list-item>
                    </v-list>
                  </div>
                </div>
              </v-list-item-subtitle>

              <template #append>
                <div class="d-flex ga-2">
                  <v-btn icon color="teal" @click="edit(p)">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon color="cyan" @click="remove(p)">
                    <v-icon>mdi-trash-can-outline</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-list-item>
          </v-list>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <!-- Context Error Codes -->
      <v-expansion-panel>
        <v-expansion-panel-title class="text-primary"> Codes d'erreur </v-expansion-panel-title>
        <v-expansion-panel-text>
          <v-progress-linear v-if="loading" indeterminate color="primary" class="mb-4" />

          <v-list v-else-if="contextErrorCodes.length > 0" density="compact">
            <v-list-item v-for="(errorCode, index) in contextErrorCodes"
              :key="errorCode.id || `${errorCode.code}-${index}`">
              <v-list-item-title class="font-weight-medium bg-grey-lighten-3 px-2 py-1 rounded">{{ errorCode.message
              }}</v-list-item-title>

              <div class="ml-2">
                <v-list-item-subtitle class="text-body-2 mt-1">
                  Code AccuTrac: {{ errorCode.accuTracCode || "-" }}
                </v-list-item-subtitle>

                <v-list-item-subtitle class="text-body-2 mt-1">
                  Code Machine: {{ errorCode.code || "-" }}
                </v-list-item-subtitle>

                <div v-if="errorCode.description" class="text-body-2 text-medium-emphasis mt-1">
                  Description: {{ errorCode.description }}
                </div>

                <div v-if="(errorCode.possibleCauses?.length ?? 0) > 0" class="mt-2">
                  <div class="text-caption font-weight-bold">Causes possibles</div>
                  <ul class="pl-4 mb-0">
                    <li v-for="(cause, causeIndex) in errorCode.possibleCauses"
                      :key="`cause-${errorCode.id || index}-${causeIndex}`" class="text-body-2">
                      {{ cause }}
                    </li>
                  </ul>
                </div>

                <div v-if="(errorCode.recommendedActions?.length ?? 0) > 0" class="mt-2">
                  <div class="text-caption font-weight-bold">Actions recommandées</div>
                  <ul class="pl-4 mb-0">
                    <li v-for="(action, actionIndex) in errorCode.recommendedActions"
                      :key="`action-${errorCode.id || index}-${actionIndex}`" class="text-body-2">
                      {{ action }}
                    </li>
                  </ul>
                </div>
              </div>

            </v-list-item>
          </v-list>

          <div v-else class="text-medium-emphasis">
            Aucun code d'erreur disponible.
          </div>
        </v-expansion-panel-text>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-title v-slot="{ expanded }">
          <v-row dense>
            <v-col cols="4" class="text-primary">Historique</v-col>
            <v-col cols="8" class="text-medium-emphasis">
              <v-fade-transition leave-absolute>
                <span v-if="expanded" key="0"> </span>
                <span v-else key="1">
                  <div v-if="product">
                    {{ historyPanelText }}
                  </div>
                </span>
              </v-fade-transition>
            </v-col>
          </v-row>
        </v-expansion-panel-title>
        <v-expansion-panel-text>
          <p v-if="sortedHistoryElements.length === 0" class="text-medium-emphasis">
            Aucun historique pour ce produit
          </p>
          <v-card v-for="he in sortedHistoryElements" :key="he.id" variant="outlined" class="mb-3">
            <v-card-item>
              <template #prepend>
                <v-avatar :color="he.isStorageDepot ? 'blue-grey' : 'primary'" variant="tonal">
                  <v-icon>{{ he.isStorageDepot ? "mdi-warehouse" : "mdi-store" }}</v-icon>
                </v-avatar>
              </template>
              <v-card-title class="text-body-1 font-weight-medium">
                {{ he.name }}
                <v-chip v-if="!he.outDate" size="x-small" color="success" variant="flat" class="ml-2">
                  Actuel
                </v-chip>
              </v-card-title>
              <v-card-subtitle>
                {{ he.isStorageDepot ? "Dépôt" : "Laverie" }} · {{ historyPeriod(he) }}
              </v-card-subtitle>
            </v-card-item>
            <v-card-text v-if="lifecycleSteps(he).length > 0" class="pt-0">
              <v-timeline density="compact" side="end" align="start" truncate-line="both">
                <v-timeline-item v-for="step in lifecycleSteps(he)" :key="step.label" :dot-color="step.color"
                  :icon="step.icon" icon-color="white" size="small">
                  <div class="d-flex flex-wrap align-baseline ga-2">
                    <span class="font-weight-medium history-step-date">{{ formatHistoryDate(step.date) }}</span>
                    <span>{{ step.label }}</span>
                  </div>
                </v-timeline-item>
              </v-timeline>
            </v-card-text>
          </v-card>
        </v-expansion-panel-text>
      </v-expansion-panel>
    </v-expansion-panels>

    <v-dialog v-model="replacementDialog" max-width="600" persistent>
      <v-card>
        <v-card-title>Remplacer le produit n° Aqua {{ product.aquaNb }}</v-card-title>
        <v-card-text>
          Le produit sera ajouté à l'activité d'enlèvement de sa laverie. Le produit de remplacement, s'il est
          choisi, sera ajouté à l'activité de préparation de la même laverie.
        </v-card-text>
        <v-card-text>
          <v-autocomplete v-model="replacementProductId" :items="replacementCandidates"
            :item-title="(item) => `${item.aquaNb} (${item.productType} ${item.productMake ?? ''})`" item-value="id"
            label="Produit de remplacement" hint="Produits en stock" persistent-hint clearable />
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" :loading="loading" @click="confirmReplacement">Confirmer</v-btn>
          <v-btn color="red" @click="replacementDialog = false">Annuler</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <ConfirmDialog v-model="removePanneDialog" :dialog-title="dialogTitle" :dialog-text="dialogText"
      @confirm="onConfirm" @cancel="onCancel" confirm-button-text="Confirmer" cancel-button-text="Annuler" />
  </v-container>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, nextTick } from "vue";
import { useRouter, useRoute } from "vue-router";
import ConfirmDialog from "@/components/ConfirmDialog.vue";
import { useAppStore } from "@/store/app";
import { ProductLifecycleStage, ProductMake, ProductStatus, ProductType, ReservationStatus } from "@/types";
import {
  getProductById,
  postProduct,
  putProduct,
  getFilteredProducts,
  getProductsByLifecycleStages,
  requestReplacement,
} from "@/utils/product-api";
import { deletePanne, getPannesByProduct } from "@/utils/panne-api";
import { getHistoryElementsByProduct } from "@/utils/product-history-element-api";
import { fetchFilteredProductDiagnosticContexts } from "@/utils/product-diagnostic-context-api";
import { getAllLaveries } from "@/utils/laverie-api";
import { getAllStorageDepots } from "@/utils/storage-depot-api";
import { fetchFilteredProductModels } from "@/utils/product-model-api";

import type { Product } from "@/types";
import type { Panne } from "@/types";
import type { Laverie } from "@/types";
import type { StorageDepot } from "@/types";
import type { ErrorCode } from "@/types";
import { HistoryElement } from "@/types";
import { ProductModel } from "@/types/product-model";

const router = useRouter();
const route = useRoute();
const props = withDefaults(defineProps<{
  productId?: string;
  embedded?: boolean;
}>(), {
  embedded: false,
});
const emit = defineEmits<{
  (event: "close"): void;
  (event: "saved", product: Product): void;
}>();
const appStore = useAppStore();

const loading = ref(false);
const hydratingProduct = ref(false);
const title = ref("");
const product = ref<Product>(createEmptyProduct());
const pannes = ref<Panne[]>([]);
const laveries = ref<Laverie[]>([]);
const storageDepots = ref<StorageDepot[]>([]);
const historyElements = ref<HistoryElement[]>([]);
const contextErrorCodes = ref<ErrorCode[]>([]);
const similarProducts = ref<Product[]>([]);
const productModels = ref<ProductModel[]>([]);

const removePanneDialog = ref(false);
const replacementDialog = ref(false);
const replacementProductId = ref<string | null>(null);
const replacementCandidates = ref<Product[]>([]);
const selectedPanne = ref<Panne | null>(null);

const dialogTitle = "Suppression";
const dialogText = "Voulez-vous supprimer cette panne ?";

const productStatus = Object.values(ProductStatus);
const reservationStatus = Object.values(ReservationStatus);
const productTypes = Object.values(ProductType);
const productMakes = Object.values(ProductMake);

const getProductId = () => props.productId ?? (route.params.id as string | undefined);
const isCreatingNewProduct = () => route.name === "newproduct" || !getProductId();
// Once created, the product only moves through its preparation, installation and removal tasks
const isLocationReadOnly = computed(() => !isCreatingNewProduct());
const locationHint = computed(() =>
  isLocationReadOnly.value
    ? "Modifiable uniquement par les activités de préparation, d'installation et d'enlèvement"
    : "Un nouveau produit entre en stock ; il est affecté à une laverie par une activité de préparation"
);

const canBeReplaced = computed(
  () => !isCreatingNewProduct() && product.value.lifecycleStage === ProductLifecycleStage.INSTALLED
);

const isDestructedReservationStatus = computed(
  () => product.value.reservationStatus === ReservationStatus.DETRUIT
);

const historyPanelText = computed(() => {
  return historyElements.value?.length ?? 0;
});

watch(
  () => product.value.productMake,
  async (productMake, previousProductMake) => {
    if (!hydratingProduct.value && productMake !== previousProductMake) {
      product.value.productModelId = undefined;
      product.value.model = "";
    }

    if (!productMake) {
      productModels.value = [];
      return;
    }

    try {
      productModels.value = await fetchFilteredProductModels(productMake);
    } catch (e) {
      productModels.value = [];
    }
  },
  { immediate: true }
);

watch(
  () => product.value.storageDepotId,
  (v) => {
    if (hydratingProduct.value) return;
    if (v) {
      product.value.reservationStatus = ReservationStatus.AVAILABLE;
      product.value.laverieId = null;
    }
  }
);

watch(
  [() => product.value.productMake, () => product.value.productModelId, () => product.value.productType
  ],
  async ([productMake, productModelId, productType]) => {
    // loadProduct() loads similar products itself once the product is hydrated
    if (hydratingProduct.value) return;
    if (!productMake || !productModelId || !productType) {
      similarProducts.value = [];
      return;
    }
    try {
      loading.value = true;
      const results = await getFilteredProducts([ReservationStatus.AVAILABLE], productMake as ProductMake, productType as ProductType, undefined, productModelId);
      similarProducts.value = results.filter((p) => p.id !== product.value.id);
    } catch (e) {
      similarProducts.value = [];
    } finally {
      loading.value = false;
    }
  },
  { immediate: true }
);

watch(
  [() => product.value.productMake, () => product.value.productType],
  async ([productMake, productType]) => {
    if (!productMake || !productType) {
      contextErrorCodes.value = [];
      return;
    }

    try {
      loading.value = true;
      const contexts = await fetchFilteredProductDiagnosticContexts(productMake, productType);
      contextErrorCodes.value = contexts.flatMap((context) => context.errorCodes || []);
    } catch (e) {
      contextErrorCodes.value = [];
      // Ignore because it's handled in axios service
    } finally {
      loading.value = false;
    }
  },
  { immediate: true }
);

function formatDate(date?: string) {
  if (!date) {
    return "-";
  }

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) {
    return date;
  }

  return parsedDate.toLocaleDateString("fr-FR");
}

function createEmptyProduct(): Product {
  return {
    id: undefined,
    laverieId: undefined,
    storageDepotId: undefined,
    model: "",
    price: 0,
    aquaNb: 0,
    serialNb: "",
    compteur: 0,
    bonEntreeNb: 0,
    bonEntreeDate: "",
    destructionDate: undefined,
    notificationEnabled: true,
    productMake: undefined,
    productType: undefined,
    productStatus: undefined,
    reservationStatus: undefined,
  } as Product;
}

const save = async () => {
  loading.value = true;
  try {
    if (!product.value.id) {
      product.value = await postProduct(product.value);
      appStore.sendSuccessNotice("New product has been added.");
      router.replace({ name: "product", params: { id: product.value.id } });
      title.value = "Editer le produit";
    } else {
      await putProduct(product.value.id, product.value);
      appStore.sendSuccessNotice("Product has been updated.");
    }
    emit("saved", product.value);
    historyElements.value = await getHistoryElementsByProduct(product.value.id);
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
};

const cancel = () => {
  if (props.embedded) {
    emit("close");
    return;
  }
  router.push({ name: "products" });
};

const edit = (p: Panne) =>
  router.push({ name: "panne", params: { productId: product.value.id, id: p.id } });

const remove = (p: Panne) => {
  selectedPanne.value = p;
  removePanneDialog.value = true;
};

const onConfirm = async () => {
  if (!selectedPanne.value) return;
  await deletePanne(selectedPanne.value.id);
  pannes.value = await getPannesByProduct(product.value.id);
  removePanneDialog.value = false;
};

const onCancel = () => {
  removePanneDialog.value = false;
  selectedPanne.value = null;
};

const createNewPanne = () => {
  if (route.params.id) {
    router.push({ name: "newpanne", params: { productId: route.params.id } });
  }
};

async function loadSimilarProducts() {
  const productMake = product.value.productMake;
  const productModelId = product.value.productModelId;
  const productType = product.value.productType;

  if (!productMake || !productModelId || !productType) {
    similarProducts.value = [];
    return;
  }

  try {
    const results = await getFilteredProducts([ReservationStatus.AVAILABLE], productMake as ProductMake, productType as ProductType, undefined, productModelId);
    similarProducts.value = results.filter((p) => p.id !== product.value.id);
  } catch (e) {
    similarProducts.value = [];
  }
}

async function openReplacementDialog() {
  replacementProductId.value = null;
  replacementDialog.value = true;
  replacementCandidates.value = await getProductsByLifecycleStages([ProductLifecycleStage.IN_STOCK]);
}

async function confirmReplacement() {
  try {
    loading.value = true;
    await requestReplacement(product.value.id as string, replacementProductId.value ?? undefined);
    replacementDialog.value = false;
    appStore.sendSuccessNotice(`Le remplacement du produit n° Aqua ${product.value.aquaNb} a été demandé`);
  } finally {
    loading.value = false;
  }
  await loadProduct(product.value.id as string);
}

// Most recent stay first
const sortedHistoryElements = computed(() =>
  [...historyElements.value].sort((a, b) => (b.inDate ?? "").localeCompare(a.inDate ?? ""))
);

function historyPeriod(he: HistoryElement): string {
  if (!he.inDate) {
    return he.outDate ? `Jusqu'au ${formatHistoryDate(he.outDate)}` : "Dates inconnues";
  }
  return he.outDate
    ? `Du ${formatHistoryDate(he.inDate)} au ${formatHistoryDate(he.outDate)}`
    : `Depuis le ${formatHistoryDate(he.inDate)}`;
}

interface LifecycleStep {
  label: string;
  date: string;
  icon: string;
  color: string;
}

// The life cycle steps the product went through in the laverie of the history element, in
// chronological order
function lifecycleSteps(he: HistoryElement): LifecycleStep[] {
  const steps: [string, string | undefined, string, string][] = [
    ["Affectation", he.assignmentDate, "mdi-link-variant", "primary"],
    ["Préparation à reprendre", he.preparationRejectionDate, "mdi-alert-circle-outline", "error"],
    ["Préparation approuvée", he.preparationApprovalDate, "mdi-check-circle-outline", "success"],
    ["Installation", he.installationDate, "mdi-washing-machine", "teal"],
    ["Validation de l'installation", he.installationValidationDate, "mdi-check-decagram", "success"],
    ["Demande d'enlèvement", he.removalRequestDate, "mdi-clock-outline", "warning"],
    ["Enlèvement", he.removalDate, "mdi-truck-outline", "orange-darken-2"],
    ["Annulation", he.cancellationDate, "mdi-cancel", "grey"],
  ];
  // The sort is stable: steps on the same day keep the order of the life cycle
  return steps
    .filter(([, date]) => !!date)
    .map(([label, date, icon, color]) => ({ label, date: date as string, icon, color }))
    .sort((a, b) => a.date.localeCompare(b.date));
}

function formatHistoryDate(date: string | undefined): string {
  return date ? new Date(date).toLocaleDateString("fr-FR") : "";
}

async function loadProduct(productId: string) {
  try {
    loading.value = true;
    title.value = "Editer le produit";

    const [p, pn, he] = await Promise.all([
      getProductById(productId),
      getPannesByProduct(productId),
      getHistoryElementsByProduct(productId),
    ]);
    hydratingProduct.value = true;
    product.value = p;
    await nextTick();
    hydratingProduct.value = false;

    pannes.value = pn;
    historyElements.value = he;

    // Load similar products after product is hydrated
    await loadSimilarProducts();
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
}

watch(
  () => route.params.id,
  async (id) => {
    if (id) {
      await loadProduct(id as string);
    }
  }
);

watch(
  () => props.productId,
  async (productId) => {
    if (productId) {
      await loadProduct(productId);
    }
  }
);

onMounted(async () => {
  try {
    loading.value = true;
    [laveries.value, storageDepots.value] = await Promise.all([
      getAllLaveries(),
      getAllStorageDepots(),
    ]);
    if (isCreatingNewProduct()) {
      title.value = "Nouveau produit";
      // There is usually a single depot, Villebon
      if (storageDepots.value.length === 1) {
        product.value.storageDepotId = storageDepots.value[0].id;
      }
      return;
    }

    const productId = getProductId();
    if (productId) {
      await loadProduct(productId);
    }
  } catch (e) {
    // Ignore because it's handled in axios service
  } finally {
    loading.value = false;
  }
});
</script>

<style scoped>
/* Aligns the labels of the life cycle steps */
.history-step-date {
  min-width: 6rem;
}

.right-section {
  min-height: 0;
  overflow: hidden;
}

.right-section-scroll {
  flex: 1 1 0;
  height: 0;
  min-height: 0;
  overflow-y: auto;
}
</style>
